import React from 'react';
import Select from './Select'; // Import the new Select component

const AuditLogsTable = ({ heading, auditLogs }) => {
	const styles = {
		tableDiv: 'bg-white rounded-lg overflow-auto mb-10',
		bottomTableHead: 'px-3.5 py-3.5 text-left text-base leading-100 font-medium text-gray',
		tableData: 'px-3.5 py-3.5 text-base font-medium text-dark-blue leading-100 text-left'
	};

	// Static audit logs for testing
	const staticAuditLogs = [
		// This month (December 2025)
		{
			activity: 'User Login',
			comment: 'Successful login',
			email: 'john.doe@example.com',
			datetime: '12/15/2025, 10:30 AM'
		},
		{
			activity: 'Document Created',
			comment: 'New document created',
			email: 'jane.smith@example.com',
			datetime: '12/14/2025, 2:45 PM'
		},
		{
			activity: 'Profile Updated',
			comment: 'User updated profile information',
			email: 'mike.jones@example.com',
			datetime: '12/10/2025, 9:15 AM'
		},
		{
			activity: 'Document Shared',
			comment: 'Document shared with team',
			email: 'sarah.wilson@example.com',
			datetime: '12/08/2025, 4:20 PM'
		},
		// Last month (November 2025)
		{
			activity: 'User Registration',
			comment: 'New user registered',
			email: 'alex.brown@example.com',
			datetime: '11/28/2025, 11:00 AM'
		},
		{
			activity: 'Document Deleted',
			comment: 'Document removed from system',
			email: 'lisa.davis@example.com',
			datetime: '11/22/2025, 3:30 PM'
		},
		{
			activity: 'Password Changed',
			comment: 'User changed password',
			email: 'robert.taylor@example.com',
			datetime: '11/15/2025, 8:45 AM'
		},
		{
			activity: 'User Logout',
			comment: 'User logged out',
			email: 'emma.white@example.com',
			datetime: '11/10/2025, 5:00 PM'
		},
		// 2-3 months ago (October 2025)
		{
			activity: 'Settings Updated',
			comment: 'System settings modified',
			email: 'admin@example.com',
			datetime: '10/25/2025, 1:15 PM'
		},
		{
			activity: 'Document Exported',
			comment: 'Document exported to PDF',
			email: 'chris.martin@example.com',
			datetime: '10/18/2025, 10:00 AM'
		},
		{
			activity: 'User Login',
			comment: 'Successful login',
			email: 'olivia.harris@example.com',
			datetime: '10/05/2025, 9:30 AM'
		},
		// 3+ months ago (September 2025)
		{
			activity: 'Account Created',
			comment: 'New account created',
			email: 'daniel.clark@example.com',
			datetime: '09/20/2025, 2:00 PM'
		}
	];

	// Use provided auditLogs or fall back to static data
	const logs = auditLogs && auditLogs.length > 0 ? auditLogs : staticAuditLogs;

	// Example options for the select
	const filterOptions = [
		{ value: 'this-month', label: 'This month' },
		{ value: 'last-month', label: 'Last month' },
		{ value: 'last-3-months', label: 'Last 3 months' }
	];

	const [filter, setFilter] = React.useState('this-month');

	// Filter audit logs based on selected time period
	const getFilteredLogs = () => {
		if (!logs || logs.length === 0) return [];

		const now = new Date();
		const currentYear = now.getFullYear();
		const currentMonth = now.getMonth();

		return logs.filter((log) => {
			if (!log.datetime) return false;

			// Parse the datetime string (assuming format like "12/15/2025, 10:30 AM" or similar)
			const logDate = new Date(log.datetime);

			// Check if date is valid
			if (isNaN(logDate.getTime())) return false;

			switch (filter) {
				case 'this-month': {
					// Show logs from current month
					return logDate.getMonth() === currentMonth && logDate.getFullYear() === currentYear;
				}
				case 'last-month': {
					// Show logs from previous month
					const lastMonth = new Date(currentYear, currentMonth - 1, 1);
					const lastMonthEnd = new Date(currentYear, currentMonth, 0);
					return logDate >= lastMonth && logDate <= lastMonthEnd;
				}
				case 'last-3-months': {
					// Show logs from last 3 months
					const threeMonthsAgo = new Date(currentYear, currentMonth - 3, 1);
					return logDate >= threeMonthsAgo && logDate <= now;
				}
				default:
					return true;
			}
		});
	};

	const filteredLogs = getFilteredLogs();

	return (
		<div>
			<div>
				<div className="flex justify-between mb-10">
					<div>
						<h2 className="text-[22px] font-medium text-dark-blue">{heading}</h2>
					</div>

					<div className="flex">
						{/* 👇 replaced native select with custom Select */}
						<Select
							id="audit-filter"
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
							options={filterOptions}
							className="min-w-[170px]"
						/>
					</div>
				</div>
				<div className={styles.tableDiv}>
					<div className="w-full">
						<table className="min-w-[680px] xl:min-w-full">
							<thead className="bg-[#F2F3F7]">
								<tr>
									<th scope="col" className={styles.bottomTableHead}>
										ACTIVITY
									</th>
									<th scope="col" className={styles.bottomTableHead}>
										COMMENT
									</th>
									<th scope="col" className={styles.bottomTableHead}>
										EMAIL
									</th>
									<th scope="col" className={styles.bottomTableHead}>
										DATE/TIME
									</th>
								</tr>
							</thead>
							<tbody className="bg-white">
								{filteredLogs.length > 0 ? (
									filteredLogs.map((log, idx) => (
										<tr key={idx}>
											<td className={styles.tableData}>{log.activity}</td>
											<td className={styles.tableData}>{log.comment}</td>
											<td className={styles.tableData}>{log.email}</td>
											<td className={styles.tableData}>{log.datetime}</td>
										</tr>
									))
								) : (
									<tr>
										<td colSpan="4" className="px-3.5 py-8 text-center text-base text-gray">
											No audit logs found for the selected period
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AuditLogsTable;
